import React, { useEffect, useState } from 'react'
import QuestionDropDown from '../QuestionDropDown'
import Box from '@mui/material/Box'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import MultipleChoiceOptions from './MultipleChoiceOptions'
import AddIcon from '@mui/icons-material/Add'
import ClearIcon from '@mui/icons-material/Clear'
import { ILinearObject, type MultiChoiceQuestion, SurveyItemQuestionType } from '../../../types'
import { useDispatch } from 'react-redux'
import CreateQuestionCardWrapper from '../CreateQuestionCardWrapper'
import { StyledBox, StyledMultipleChoiceWrapper, StyledOptionWrapper, StyledQuestionWrapper, StyledTextField } from './styles'
import { ButtonSize, ButtonType } from '../../../../../components/GenericButton/styles'
import GenericButton from '../../../../../components/GenericButton'
import { addFollowUpFreeResponseQuestionInSection, addSurveyQuestionInSection, type ISurveySection, setSelectedQuestion, updateBranchFilterAnswers, updateBranchFilterPrompt, updateQuestionInSection } from '../../../../../store/slices/SurveyTemplateSlice'
import { getEditableQuestion, getSelectedQuestion } from '../../../../../store/slices/SurveyTemplateSlice/selectors'
import QuestionFooter from '../../QuestionFooter'
import { useTheme } from '@mui/material/styles'
import { blue, grey } from '../../../../../colors'
import { v4 as uuidv4 } from 'uuid'
import { checkQuestionOptionHaveBranching, getUniqueChildBranchesDetails } from '../../../helpers'
import { Typography } from '@mui/material'

interface CreateMultipleChoiceQuestionCardProps {
  sectionIndex?: number
  questionIndex?: number
  questionNumber?: number
  section: ISurveySection
  isAdminAddedQuestion?: boolean
  isCountComplete?: boolean
}

export const NOT_APPLICABLE = 'Not Applicable'

const CreateMultipleChoiceQuestionCard: React.FC<CreateMultipleChoiceQuestionCardProps> = ({ sectionIndex = 0, questionIndex = 0, questionNumber, section, isAdminAddedQuestion, isCountComplete }): JSX.Element => {
  const dispatch = useDispatch()
  const editableQuestion = getEditableQuestion()
  const selectedQuestion = getSelectedQuestion()

  const theme = useTheme()

  const [multipleChoiceCardData, setMultipleChoiceCardData] = useState<MultiChoiceQuestion>({
    itemId: uuidv4(),
    itemType: SurveyItemQuestionType.MULTIPLE_CHOICE,
    prompt: '',
    object: ['', ''],
    isRequired: false,
    branchingFilters: null
  })

  const isQuestionHaveBranchFilters = !!multipleChoiceCardData?.branchingFilters?.length

  useEffect(() => {
    if (editableQuestion != null) {
      setMultipleChoiceCardData(editableQuestion?.question as MultiChoiceQuestion)
      return
    }
    selectedQuestion?.question && setMultipleChoiceCardData(selectedQuestion?.question as MultiChoiceQuestion)
  }, [editableQuestion, dispatch])

  const handleQuestionTitle = (questionTitle: string): void => {
    // Change Question Prompt in child Branch Section Branching Filters
    if (multipleChoiceCardData?.branchingFilters) {
      dispatch(updateBranchFilterPrompt({
        childBranches: getUniqueChildBranchesDetails(multipleChoiceCardData.branchingFilters),
        prompt: questionTitle
      }))
    }

    setMultipleChoiceCardData(prevState => {
      return { ...prevState, prompt: questionTitle }
    })
  }

  const addNewOption = (): void => {
    setMultipleChoiceCardData((prevState: MultiChoiceQuestion) => {
      const newOptions = [...prevState?.object]
      newOptions.push('')

      const updatedData = newOptions?.sort((a: string, b: string) => {
        if (a === NOT_APPLICABLE) return 1
        if (b === NOT_APPLICABLE) return -1

        return newOptions.indexOf(a) - newOptions.indexOf(b)
      })

      return { ...prevState, object: updatedData }
    })
  }

  const RemoveOption = (index: number): void => {
    // If Question Option have BranchSection  don't Delete show Alert message
    if (checkQuestionOptionHaveBranching(index, multipleChoiceCardData)) {
      return
    }

    setMultipleChoiceCardData(prevState => {
      const newOptions = [...prevState.object]
      newOptions.splice(index, 1)
      return { ...prevState, object: newOptions }
    })
  }

  const handleUpdate = (index: number, option: string): void => {
    setMultipleChoiceCardData(prevState => {
      const newOptions = [...prevState.object]

      if (option !== '') {
        newOptions[index] = option
      } else {
        newOptions[index] = ''
      }

      // If Selected Option have branch section Update that as well
      const oldKey = prevState.object[index]
      const updatedBranchingFilters = prevState.branchingFilters?.map(filter =>
        filter.key === oldKey
          ? { ...filter, key: option }
          : filter
      )
      // Update Child Branch section answers
      const payload = { oldKey, newKey: option, parentQuestionId: prevState.itemId, parentSurveySectionId: section.surveySectionId }
      dispatch(updateBranchFilterAnswers(payload))
      return {
        ...prevState,
        object: newOptions,
        branchingFilters: updatedBranchingFilters ?? multipleChoiceCardData.branchingFilters
      }
    })
  }

  const handleRequired = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean): void => {
    setMultipleChoiceCardData(prevState => {
      return { ...prevState, isRequired: checked }
    })
  }

  const handleClickAway = (): void => {
    if (editableQuestion) {
      dispatch(updateQuestionInSection({ surveySectionId: section.surveySectionId, questionIndex, updatedQuestion: multipleChoiceCardData }))
      return
    }

    dispatch(addSurveyQuestionInSection({ surveySectionId: section.surveySectionId, question: multipleChoiceCardData }))
    dispatch(setSelectedQuestion(null))
  }

  const handleDuplicate = (): void => {
    // Duplicate question without isDirty
    const question = {
      itemId: uuidv4(),
      itemType: SurveyItemQuestionType.MULTIPLE_CHOICE,
      prompt: '',
      object: multipleChoiceCardData.object,
      isRequired: multipleChoiceCardData.isRequired,
      branchingFilters: null
    }
    if (editableQuestion) {
      // Update the current question in section
      dispatch(updateQuestionInSection({ surveySectionId: section.surveySectionId, questionIndex, updatedQuestion: multipleChoiceCardData }))
      dispatch(setSelectedQuestion({ sectionIndex, questionType: SurveyItemQuestionType.MULTIPLE_CHOICE, question }))
    } else {
      // New question: save it first, then set up the duplicate
      dispatch(addSurveyQuestionInSection({ surveySectionId: section.surveySectionId, question: multipleChoiceCardData }))
      dispatch(setSelectedQuestion({ sectionIndex, questionType: SurveyItemQuestionType.MULTIPLE_CHOICE, question }))
    }
  }

  const handleAddFollowUp = (): void => {
    if (editableQuestion) {
      dispatch(updateQuestionInSection({ surveySectionId: section.surveySectionId, questionIndex, updatedQuestion: multipleChoiceCardData }))
      dispatch(addFollowUpFreeResponseQuestionInSection({ surveySectionId: section.surveySectionId, followUpParentQuestionIndex: questionIndex, questionTitle: multipleChoiceCardData.prompt }))
      dispatch(setSelectedQuestion(null))
    }
  }

  const addNewOptionNotApplicable = (): void => {
    setMultipleChoiceCardData((prevState: MultiChoiceQuestion) => {
      const newOptions = [...prevState?.object]
      newOptions.push(NOT_APPLICABLE)
      return { ...prevState, object: newOptions }
    })
  }
  const hideNotApplicable = multipleChoiceCardData?.object?.find(value => value === NOT_APPLICABLE)

  const footerPropsData = {
    questionIndex,
    questionData: multipleChoiceCardData,
    handleChange: handleRequired,
    handleDuplicate,
    handleClickAway,
    handleAddFollowUp,
    section,
    isAdminAddedQuestion,
    isCountComplete
  }

  return (
    <ClickAwayListener
      mouseEvent="onMouseDown"
      touchEvent="onTouchStart"
      onClickAway={handleClickAway}>
      <StyledBox >
        <CreateQuestionCardWrapper isAdminAddedQuestion={isAdminAddedQuestion} questionNumber={questionNumber} questionData={multipleChoiceCardData} section={section}>
          <>
            <StyledQuestionWrapper >
              <StyledTextField value={multipleChoiceCardData?.prompt} type='text' placeholder='Type your question...' autoFocus onChange={(e) => { handleQuestionTitle(e.target.value) }} />
              <QuestionDropDown section={section} sectionIndex={sectionIndex} questionIndex={questionIndex} isQuestionHaveBranchFilters={isQuestionHaveBranchFilters} />
            </StyledQuestionWrapper>
            <StyledMultipleChoiceWrapper >
              {multipleChoiceCardData?.object?.map((option: string, index: number) => (
                <StyledOptionWrapper key={index}>
                  <MultipleChoiceOptions option={option} index={index} handleUpdate={handleUpdate} />
                  <GenericButton
                    buttonSize={ButtonSize.Small}
                    buttonType={ButtonType.Ghost}
                    icon={<ClearIcon color='action' />}
                    onClick={() => { RemoveOption(index) }}
                    disabled={multipleChoiceCardData?.object?.length <= 2} />
                </StyledOptionWrapper>
              ))}
            </StyledMultipleChoiceWrapper>

            <Box display='flex' gap={theme.spacing(0)} mt={theme.spacing(4)} alignItems='center'>
              <GenericButton
                buttonType={ButtonType.Ghost}
                startIcon={<AddIcon sx={{ fontWeight: 500 }} />}
                onClick={() => { addNewOption() }}
                label='Add Option'
                buttonSize={ButtonSize.Medium}
                style={{
                  fontSize: '16px',
                  fontWeight: 500,
                  color: grey[850],
                  padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`
                }}
              />

              {
                !hideNotApplicable &&
                <>
                  <Typography sx={{ fontSize: '16px', fontWeight: 500, color: grey[850] }}>or</Typography>
                  <GenericButton
                    buttonType={ButtonType.Ghost}
                    onClick={() => { addNewOptionNotApplicable() }}
                    label={NOT_APPLICABLE}
                    buttonSize={ButtonSize.Medium}
                    style={{
                      fontSize: '16px',
                      fontWeight: 500,
                      color: blue[500],
                      padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`

                    }}
                  />
                </>
              }
            </Box>
            <QuestionFooter {...footerPropsData} />
          </>
        </CreateQuestionCardWrapper>
      </StyledBox>
    </ClickAwayListener>
  )
}

export default CreateMultipleChoiceQuestionCard
